/**
 * The calendar's measures and fills, as Boards 2–4, 13 and 18 draw them
 * (`docs/activity.md` §6.1–§6.4). Not status colours — the grid's own
 * furniture, kept here once so the grid, the week rows, Month and the phone
 * cannot drift apart.
 */

/** The past is a shade greyer (Aaron, point 3; do-not-substitute 21). */
export const PAST_TINT = 'rgba(96,94,84,.055)'
/** A day the person does not work, dimmed (§6.1). */
export const OFF_TINT = 'rgba(246,246,242,.6)'
/** A time block's hatching (§6.2). */
export const HATCH = 'repeating-linear-gradient(-45deg,rgb(244 243 239),rgb(244 243 239) 6px,rgb(236 235 230) 6px,rgb(236 235 230) 12px)'
/** Free for all (§6.4.2). */
export const FREE_HATCH = 'repeating-linear-gradient(-45deg,rgb(var(--color-accent-soft)),rgb(var(--color-accent-soft)) 6px,rgb(234 245 238) 6px,rgb(234 245 238) 12px)'
/** The now line and its dot (§6.1). */
export const NOW_RED = 'rgb(180 35 24)'
/** A timed task's chip (§6.2). */
export const TASK_LINE = 'rgb(240 223 191)'
export const TASK_BOX = 'rgb(201 138 30)'
export const TASK_TEXT = 'rgb(122 79 16)'

/** 1 px a minute on a desktop (§6.1). */
export const PX_PER_MINUTE = 1
/** The time axis. */
export const AXIS_WIDTH = 48
/** The grid's own padding above and below the hours (§6.1). */
export const GRID_TOP = 10
export const GRID_BOTTOM = 12
/** Every day header is the same height (§6.1). */
export const HEAD_HEIGHT = 52
/**
 * The grid at each layout, measured off the boards: a minute is a pixel on a
 * desktop (Board 2), 54 px an hour on an iPad on its side (Board 12 A), 84 on
 * one held upright (Board 12 B) and 62.5 on a phone (Board 13 B); the axis,
 * the day headers and a person's column narrow with it.
 */
export interface GridMetrics {
  scale: number
  axis: number
  head: number
  /** Day side by side: one person's column. */
  personColumn: number
}

export const GRID_METRICS = {
  desktop: { scale: 1, axis: 48, head: 52, personColumn: 220 },
  landscape: { scale: 0.9, axis: 48, head: 44, personColumn: 220 },
  portrait: { scale: 1.4, axis: 44, head: 52, personColumn: 240 },
  phone: { scale: 62.5 / 60, axis: 38, head: 36, personColumn: 106 },
} as const satisfies Record<string, GridMetrics>

/** A block is drawn 2 px shorter than its minutes, so two in a row show a gap. */
export const BLOCK_GAP = 2

/** *9:00–10:00*, *12:30–1:30* — 12-hour, no AM/PM, as the boards print the time row. */
export function clock(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')}`
}

export const span = (start: number, end: number): string => `${clock(start)}–${clock(end)}`

/** *7 AM*, *12 PM* — the axis. */
export function hourWords(hour: number): string {
  const h = ((hour + 11) % 12) + 1
  return `${h} ${hour < 12 ? 'AM' : 'PM'}`
}
